/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC } from "react";

import styles from "./AudioCallView.module.css";
import { SpeakingRipple } from "../tile/SpeakingRipple";
import { Avatar } from "../Avatar";
import { useBehavior } from "../useBehavior";
import { constant } from "../state/Behavior";
import { type WrappedUserMediaViewModel } from "../state/media/WrappedUserMediaViewModel";

interface AvatarItemProps {
  vm: WrappedUserMediaViewModel;
  className?: string;
}

const AvatarItem: FC<AvatarItemProps> = ({ vm, className }) => {
  const name = useBehavior(vm.displayName$);
  const avatarUrl = useBehavior(vm.mxcAvatarUrl$);
  return (
    <Avatar
      id={vm.id}
      name={name}
      src={avatarUrl}
      size={100}
      className={className}
    />
  );
};

interface Props {
  /** The other participants in the call (excludes the local user). */
  participants: WrappedUserMediaViewModel[];
  /**
   * The local user's own media, shown as a placeholder in the center while
   * no one else has joined yet, so the screen never renders empty.
   */
  localParticipant: WrappedUserMediaViewModel | undefined;
}

/**
 * The dedicated audio-only call screen: a centered avatar (or, for group
 * calls, two overlapping avatars) surrounded by speaking-reactive ripple
 * rings, replacing the video tile grid entirely when no one has their
 * camera on. Matches the Figma "audio call" / "group audio call" designs.
 */
export const AudioCallView: FC<Props> = ({ participants, localParticipant }) => {
  const [first, second] = participants;
  const isGroup = participants.length > 1;
  // While alone in the call, show our own avatar rather than an empty ring.
  const solo = first ?? localParticipant;

  const firstSpeaking = useBehavior(first?.speaking$ ?? constant(false));
  const secondSpeaking = useBehavior(second?.speaking$ ?? constant(false));
  const speaking = firstSpeaking || secondSpeaking;

  return (
    <div className={styles.container}>
      <div className={styles.avatarArea}>
        <SpeakingRipple speaking={speaking} />
        {isGroup ? (
          <div className={styles.avatarPair}>
            {second && (
              <AvatarItem vm={second} className={styles.avatarBack} />
            )}
            {first && (
              <AvatarItem vm={first} className={styles.avatarFront} />
            )}
          </div>
        ) : (
          solo && <AvatarItem vm={solo} className={styles.avatarSolo} />
        )}
      </div>
    </div>
  );
};
